import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Target, 
  Puzzle, 
  Globe, 
  TrendingUp, 
  Users, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Building2,
  ExternalLink
} from 'lucide-react';

export default function About() {
  return (
    <Layout>
      <div className="container py-12 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">Our Mission</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Why We're Building AutomotiveAPI
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            The automotive industry deserves a universal language for data. 
            We're creating the open standard that makes integration seamless.
          </p>
        </div>

        {/* The Problem */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-destructive/10">
              <Puzzle className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold">The Problem</h2>
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-lg mb-3">Fragmented Data Formats</h3>
                  <p className="text-muted-foreground mb-4">
                    Every DMS, CRM, and inventory system speaks its own language. 
                    A "customer" in one system doesn't match a "contact" in another. 
                    Even ADF—the industry's 20-year-old lead format—lacks the tooling modern teams need.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">✗</span>
                      <span className="text-muted-foreground">Custom integrations for every vendor pair</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">✗</span>
                      <span className="text-muted-foreground">Data loss and corruption during transfers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">✗</span>
                      <span className="text-muted-foreground">Months of development for basic connections</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-3">The Cost of Chaos</h3>
                  <p className="text-muted-foreground mb-4">
                    Dealerships and vendors spend millions building one-off integrations 
                    that break with every update. Innovation stalls while teams maintain 
                    fragile data pipelines.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">✗</span>
                      <span className="text-muted-foreground">Vendor lock-in from proprietary formats</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">✗</span>
                      <span className="text-muted-foreground">Duplicated engineering effort across the industry</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-destructive mt-1">✗</span>
                      <span className="text-muted-foreground">Slow adoption of new technologies</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* The Vision */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-primary/10">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Our Vision</h2>
          </div>
          
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <blockquote className="text-xl md:text-2xl font-medium text-center italic mb-6">
                "One universal standard for automotive data—open, free, and community-driven."
              </blockquote>
              <p className="text-center text-muted-foreground max-w-2xl mx-auto">
                We envision an industry where any system can talk to any other system 
                out of the box. Where switching vendors is a configuration change, 
                not a six-month project. Where innovation happens at the speed of ideas.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* The Solution */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Zap className="h-6 w-6 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold">The Solution</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Open Standards</h3>
                <p className="text-muted-foreground">
                  Publicly documented JSON schemas that anyone can implement. 
                  No licensing fees, no restrictions, no gatekeepers.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Community-Driven</h3>
                <p className="text-muted-foreground">
                  Built by the people who use it. Dealers, vendors, and developers 
                  collaborate to define standards that work in the real world.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Validated & Tested</h3>
                <p className="text-muted-foreground">
                  Every standard comes with validation tools—including ADF. Test your data 
                  before sending it, whether you're using legacy formats or modern APIs.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Impact */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <TrendingUp className="h-6 w-6 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold">The Impact</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Reduced Integration Costs</h4>
                    <p className="text-sm text-muted-foreground">
                      Build once, connect everywhere. No more custom mappings for each vendor.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Faster Development</h4>
                    <p className="text-sm text-muted-foreground">
                      Ship integrations in days, not months. Focus on features, not data translation.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Fewer Errors</h4>
                    <p className="text-sm text-muted-foreground">
                      Validated data means fewer support tickets and cleaner databases.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Vendor Freedom</h4>
                    <p className="text-sm text-muted-foreground">
                      Switch systems without losing data fidelity. Your data, your choice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Industry Innovation</h4>
                    <p className="text-sm text-muted-foreground">
                      New vendors can plug in immediately. Competition drives better products.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Future-Proof</h4>
                    <p className="text-sm text-muted-foreground">
                      Standards evolve with versioning. Migrate at your own pace.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Legacy Modernization</h4>
                    <p className="text-sm text-muted-foreground">
                      Better tooling for ADF today, with a clear path to API-based standards.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Who's Behind This */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Building2 className="h-6 w-6 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold">Who's Behind This</h2>
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <h3 className="font-semibold text-xl mb-3">More Than Cars</h3>
                  <p className="text-muted-foreground mb-4">
                    AutomotiveAPI is built by More Than Cars—a movement disguised as a media company, 
                    with the spirit of a tech start-up and the heart of a leader who believes in the 
                    people of automotive retail.
                  </p>
                  <p className="text-muted-foreground mb-6">
                    Through our media network, creative shop, and consulting practice, we've seen 
                    firsthand how fragmented data holds back the industry. AutomotiveAPI is our 
                    contribution to solving that problem—open, free, and built for the community.
                  </p>
                  <Button asChild variant="outline">
                    <a 
                      href="https://www.morethancars.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Learn More About Us
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Card className="bg-muted/50">
            <CardContent className="py-12">
              <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Explore standards, build schemas, submit proposals, and help shape 
                the future of automotive data.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg">
                  <Link to="/api-standards">
                    Browse Standards
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/partners">
                    Meet Our Partners
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
